import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import AuctionCard from "@/components/AuctionCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowRight, Shield, TrendingUp, Zap, Search, Bell, CheckCircle2, MapPin, Clock } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1649313444539-a8900c5cdc54?w=1920&auto=format&fit=crop&q=80";

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState("");
  const [waitlistCount, setWaitlistCount] = useState(0);
  const nav = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get("/auctions/featured?limit=6").then((r) => setFeatured(r.data)).catch(() => {}),
      api.get("/waitlist/count").then((r) => setWaitlistCount(r.data?.count || 0)).catch(() => {}),
    ]).finally(() => setLoaded(true));
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    const q = search.trim();
    nav(q ? `/browse?q=${encodeURIComponent(q)}` : "/browse");
  };

  // Pre-launch mode: no live auctions yet → show waitlist capture instead of search.
  // Auto-switches to live mode as soon as the first auction is listed.
  const preLaunch = loaded && featured.length === 0;

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden grain border-b border-zinc-900">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-br from-black via-black/70 to-black/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 lg:py-40">
          <div className="max-w-3xl">
            {preLaunch ? (
              <LaunchBadge />
            ) : (
              <div className="inline-flex items-center gap-2 bg-black/70 border border-orange-500/40 px-3 py-1 mb-6">
                <span className="w-1.5 h-1.5 bg-orange-500 live-dot rounded-full" />
                <span className="text-[10px] font-semibold tracking-[0.3em] uppercase text-orange-400">
                  Live Auctions Now
                </span>
              </div>
            )}

            {preLaunch ? (
              <h1 data-testid="hero-headline" className="font-display text-5xl sm:text-6xl lg:text-7xl font-black uppercase tracking-tighter leading-[0.9] text-white">
                Storage <span className="text-orange-500">Auctions</span><br />
                Listing Soon.<br />
                <span className="text-zinc-400">Be first in line.</span>
              </h1>
            ) : (
              <h1 data-testid="hero-headline" className="font-display text-5xl sm:text-6xl lg:text-7xl font-black uppercase tracking-tighter leading-[0.9] text-white">
                Bid on <span className="text-orange-500">Abandoned</span><br />
                Storage Units.<br />
                <span className="text-zinc-400">Uncover Treasure.</span>
              </h1>
            )}

            <p className="mt-6 text-base sm:text-lg text-zinc-300 leading-relaxed max-w-xl">
              {preLaunch
                ? "We're onboarding self-storage facilities right now. Join the waitlist and get a 24-hour head start on every unit — bid before we open to the public."
                : "StorageVault is the online auction platform for self-storage facilities. Real units. Real photos. Real bids. Winners collect their contents onsite."}
            </p>

            {preLaunch ? (
              <WaitlistForm onJoined={() => setWaitlistCount((n) => n + 1)} />
            ) : (
              <form onSubmit={onSearch} className="mt-10 flex flex-col sm:flex-row gap-3 max-w-2xl">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    data-testid="hero-search-input"
                    placeholder="Search by city, facility, or unit size…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-11 h-14 rounded-none bg-zinc-950/80 border-zinc-700 focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-0 industrial-input text-base"
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="hero-search-button"
                  className="rounded-none bg-orange-600 hover:bg-orange-500 border border-orange-500 h-14 px-8 text-sm uppercase tracking-widest font-semibold"
                >
                  Search Auctions <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </form>
            )}

            {preLaunch ? (
              <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
                <Stat value={waitlistCount.toLocaleString()} label="On the Waitlist" testid="waitlist-count-stat" />
                <Stat value="24H" label="Early-Access Head Start" />
                <Stat value="0%" label="Sniper Fees" />
              </div>
            ) : (
              <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
                <Stat value="1,247" label="Live Auctions" />
                <Stat value="$2.4M" label="Paid to Facilities" />
                <Stat value="45K+" label="Verified Bidders" />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FEATURED AUCTIONS or WHAT TO EXPECT */}
      {preLaunch ? (
        <WhatToExpect />
      ) : (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-orange-500 mb-2">Hot Right Now</div>
              <h2 className="font-display text-3xl sm:text-5xl font-black uppercase tracking-tighter">
                Featured Auctions
              </h2>
            </div>
            <Link
              to="/browse"
              data-testid="view-all-auctions"
              className="hidden sm:inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-orange-500 border-b border-zinc-700 hover:border-orange-500 pb-1 transition-colors"
            >
              View All <ArrowRight className="w-3 h-3" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {featured.map((a, i) => (
              <AuctionCard key={a.id} auction={a} index={i} />
            ))}
          </div>
        </section>
      )}

      {/* HOW IT WORKS */}
      <section className="border-t border-zinc-900 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-[10px] tracking-[0.3em] uppercase text-orange-500 mb-2">Process</div>
          <h2 className="font-display text-3xl sm:text-5xl font-black uppercase tracking-tighter mb-16 max-w-2xl">
            Three steps from bid<br />to <span className="text-orange-500">warehouse floor.</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Step n="01" title="Browse & Bid" desc="Search live auctions by state, city, or unit size. Review photos and place your bids before the timer hits zero." />
            <Step n="02" title="Win & Pay" desc="Highest bidder at close pays winning bid + buyer's premium via secure Stripe checkout. No hidden fees." />
            <Step n="03" title="Collect Onsite" desc="Show your receipt at the facility within 48 hours. Take the entire contents. Bring a truck." />
          </div>
        </div>
      </section>

      {/* TRUST SIGNALS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 border-t border-zinc-900">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Feature icon={<Shield className="w-6 h-6" />} title="Verified Facilities" desc="Every seller is a licensed self-storage operator with lien-holder rights. No sketchy listings." />
          <Feature icon={<Zap className="w-6 h-6" />} title="Auto-Extend Timers" desc="Last-second bids extend the auction by 5 minutes. Fair endings, no snipers." />
          <Feature icon={<TrendingUp className="w-6 h-6" />} title="Real-Time Bids" desc="Live bid ticker, instant outbid alerts, and full bid history on every listing." />
        </div>
      </section>

      {/* SELLER CTA */}
      <section className="border-t border-zinc-900 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-orange-500 mb-2">For Facilities</div>
            <h2 className="font-display text-3xl sm:text-5xl font-black uppercase tracking-tighter max-w-xl">
              Recover lien value.<br /><span className="text-orange-500">Zero listing fees.</span>
            </h2>
            {preLaunch && (
              <p className="mt-4 text-sm text-zinc-400 max-w-xl">
                Launch partners get <b className="text-orange-500">$75 in listing credits</b> (code <b className="font-mono text-white">VAULT25</b>) and priority placement in front of our growing waitlist.
              </p>
            )}
          </div>
          <Button
            asChild
            className="rounded-none bg-orange-600 hover:bg-orange-500 border border-orange-500 h-14 px-8 text-sm uppercase tracking-widest font-semibold"
          >
            <Link to="/register" data-testid="cta-become-seller">Become a Seller <ArrowRight className="w-4 h-4 ml-2" /></Link>
          </Button>
        </div>
      </section>
    </div>
  );
}

function LaunchBadge() {
  return (
    <div className="inline-flex items-center gap-2 bg-black/70 border border-orange-500/40 px-3 py-1 mb-6">
      <Clock className="w-3 h-3 text-orange-500" />
      <span data-testid="launch-badge" className="text-[10px] font-semibold tracking-[0.3em] uppercase text-orange-400">
        Auctions Listing Soon
      </span>
    </div>
  );
}

function WaitlistForm({ onJoined }) {
  const [email, setEmail] = useState("");
  const [zip, setZip] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [joined, setJoined] = useState(null); // null | {position}

  const submit = async (e) => {
    e.preventDefault();
    const cleaned = email.trim().toLowerCase();
    if (!cleaned || !cleaned.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/waitlist/join", {
        email: cleaned,
        zip_code: zip.trim() || null,
        source: "homepage",
      });
      setJoined({ position: data.position, already: data.already_joined });
      if (!data.already_joined) onJoined?.();
      toast.success(
        data.already_joined
          ? `You're already on the list at #${data.position}`
          : `You're #${data.position} on the list — check your inbox`
      );
    } catch (err) {
      toast.error(formatError(err) || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  if (joined) {
    return (
      <div data-testid="waitlist-success" className="mt-10 max-w-2xl bg-emerald-500/5 border border-emerald-500/40 p-6">
        <div className="flex items-start gap-4">
          <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-display text-xl font-bold uppercase tracking-tight text-white">
              You're #{joined.position} on the list
            </div>
            <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
              {joined.already
                ? "You were already signed up. We'll email you the moment auctions go live."
                : "Check your inbox — we sent a confirmation. When the first auctions drop, waitlist members get a 24-hour head start before we open to the public."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-10 flex flex-col sm:flex-row gap-3 max-w-2xl">
      <div className="relative flex-1">
        <Bell className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <Input
          data-testid="waitlist-email-input"
          type="email"
          required
          placeholder="your@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="pl-11 h-14 rounded-none bg-zinc-950/80 border-zinc-700 focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-0 industrial-input text-base"
        />
      </div>
      <div className="relative w-full sm:w-40">
        <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <Input
          data-testid="waitlist-zip-input"
          placeholder="ZIP (opt)"
          value={zip}
          onChange={(e) => setZip(e.target.value)}
          maxLength={5}
          className="pl-11 h-14 rounded-none bg-zinc-950/80 border-zinc-700 focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-0 industrial-input text-base"
        />
      </div>
      <Button
        type="submit"
        disabled={submitting}
        data-testid="waitlist-submit-button"
        className="rounded-none bg-orange-600 hover:bg-orange-500 border border-orange-500 h-14 px-8 text-sm uppercase tracking-widest font-semibold"
      >
        {submitting ? "Joining…" : (<>Notify Me <ArrowRight className="w-4 h-4 ml-2" /></>)}
      </Button>
    </form>
  );
}

function WhatToExpect() {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
      <div className="mb-12">
        <div className="text-[10px] tracking-[0.3em] uppercase text-orange-500 mb-2">What to Expect</div>
        <h2 className="font-display text-3xl sm:text-5xl font-black uppercase tracking-tighter max-w-3xl">
          Every unit. <span className="text-orange-500">Full photos.</span><br />
          Real facilities. <span className="text-zinc-400">Fair auctions.</span>
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Expect
          n="Real Photos"
          title="See what you're bidding on"
          desc="Every listing includes clear photos from inside the unit. No blurry teasers. No mystery boxes hidden behind the door."
        />
        <Expect
          n="Fair Timers"
          title="Auto-extend on last-second bids"
          desc="If someone bids in the last 5 minutes, the clock resets by 5 minutes. Snipers can't win by camping the countdown."
        />
        <Expect
          n="Verified Sellers"
          title="Licensed facilities only"
          desc="Every seller is a licensed self-storage operator with legal lien-holder rights. Every unit is a real defaulted unit."
        />
      </div>
    </section>
  );
}

function Expect({ n, title, desc }) {
  return (
    <div className="bg-zinc-950 border border-zinc-800 hover:border-orange-500 transition-colors p-8">
      <div className="text-[10px] tracking-[0.3em] uppercase text-orange-500">{n}</div>
      <h3 className="mt-4 font-display text-2xl font-bold uppercase tracking-tight text-white">{title}</h3>
      <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{desc}</p>
    </div>
  );
}

function Stat({ value, label, testid }) {
  return (
    <div className="border-l-2 border-orange-500 pl-4">
      <div data-testid={testid} className="font-display text-3xl sm:text-4xl font-black text-white tabular-nums">{value}</div>
      <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mt-1">{label}</div>
    </div>
  );
}

function Step({ n, title, desc }) {
  return (
    <div className="bg-black border border-zinc-800 hover:border-orange-500 transition-colors p-8">
      <div className="font-display text-6xl font-black text-orange-500 tabular-nums leading-none">{n}</div>
      <h3 className="mt-6 font-display text-2xl font-bold uppercase tracking-tight">{title}</h3>
      <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{desc}</p>
    </div>
  );
}

function Feature({ icon, title, desc }) {
  return (
    <div className="p-6 border border-zinc-800">
      <div className="w-12 h-12 bg-zinc-900 border border-zinc-800 flex items-center justify-center text-orange-500">{icon}</div>
      <h3 className="mt-6 font-display text-xl font-bold uppercase tracking-tight">{title}</h3>
      <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{desc}</p>
    </div>
  );
}
